import React, { useEffect, useState } from "react";
import { Download, Share, SquarePlus, X, MoreVertical } from "lucide-react";

const INSTALLED_KEY = "ag7_pwa_installed";
const DISMISS_KEY = "ag7_pwa_install_dismissed";
const SNOOZE_DAYS = 7;
const MAX_DISMISSALS = 3;
const SHOW_DELAY_MS = 4000;

const ls = {
  get: (k) => { try { return window.localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* noop */ } },
};

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches
  || window.matchMedia?.("(display-mode: fullscreen)").matches
  || window.navigator.standalone === true
  || new URLSearchParams(window.location.search).get("source") === "pwa";

export const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || iPadOS) {
    const safari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
    return { os: "ios", safari };
  }
  if (/Android/i.test(ua)) return { os: "android", safari: false };
  const macSafari = /Macintosh/i.test(ua) && /Safari/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua);
  return { os: "desktop", safari: macSafari };
};

export const shouldHidePrompt = () => {
  if (isStandalone() || ls.get(INSTALLED_KEY) === "1") return true;
  try {
    const d = JSON.parse(ls.get(DISMISS_KEY) || "null");
    if (!d) return false;
    if ((d.count || 0) >= MAX_DISMISSALS) return true;
    return Date.now() - (d.at || 0) < SNOOZE_DAYS * 86400000;
  } catch { return false; }
};

/** Aviso discreto (no bloquea) para instalar AG7 Store como app. */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform] = useState(detectPlatform);

  useEffect(() => {
    if (shouldHidePrompt()) return undefined;
    const onBip = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { ls.set(INSTALLED_KEY, "1"); setVisible(false); };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Sólo se muestra si hay una vía real de instalación en este navegador.
  const canNativePrompt = !!deferred;
  const iosGuide = platform.os === "ios" && platform.safari;
  const iosOtherBrowser = platform.os === "ios" && !platform.safari;
  const macSafariGuide = platform.os === "desktop" && platform.safari;
  if (!visible || !(canNativePrompt || iosGuide || iosOtherBrowser || macSafariGuide)) return null;

  const dismiss = () => {
    let d = { count: 0 };
    try { d = JSON.parse(ls.get(DISMISS_KEY) || "{}") || {}; } catch { /* noop */ }
    ls.set(DISMISS_KEY, JSON.stringify({ at: Date.now(), count: (d.count || 0) + 1 }));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") { ls.set(INSTALLED_KEY, "1"); setVisible(false); }
    else dismiss();
  };

  return (
    <div
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Instalar AG7 Store"
      className="fixed z-[60] left-3 right-3 bottom-3 sm:left-auto sm:right-5 sm:bottom-5 sm:w-[360px] rounded-2xl border border-ag7-purple/40 bg-ag7-surface/95 backdrop-blur-md shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-3.5 flex items-start gap-3 animate-in slide-in-from-bottom-4 fade-in duration-500"
    >
      <img src="/icons/icon-192.png" alt="" className="w-11 h-11 rounded-xl flex-shrink-0 border border-[var(--ag7-border-soft)]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-heading font-extrabold text-white leading-tight">Instalar AG7 Store</p>
        {canNativePrompt ? (
          <p className="text-[11px] text-zinc-400 mt-0.5">Acceso directo en tu pantalla de inicio, más rápido y sin barra del navegador.</p>
        ) : iosGuide ? (
          <p className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed" data-testid="pwa-ios-guide">
            Toca <Share className="inline w-3.5 h-3.5 -mt-0.5 text-ag7-cyan" /> <strong className="text-zinc-200">Compartir</strong> y luego
            <SquarePlus className="inline w-3.5 h-3.5 -mt-0.5 mx-1 text-ag7-cyan" /><strong className="text-zinc-200">Agregar a pantalla de inicio</strong>.
          </p>
        ) : iosOtherBrowser ? (
          <p className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed" data-testid="pwa-ios-other-guide">
            En iPhone/iPad ábrela en <strong className="text-zinc-200">Safari</strong>, toca <Share className="inline w-3.5 h-3.5 -mt-0.5 text-ag7-cyan" /> Compartir y <strong className="text-zinc-200">Agregar a pantalla de inicio</strong>.
          </p>
        ) : (
          <p className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed" data-testid="pwa-mac-guide">
            En Safari: menú <strong className="text-zinc-200">Archivo</strong> → <strong className="text-zinc-200">Agregar al Dock</strong>
            <MoreVertical className="inline w-3 h-3 -mt-0.5 ml-1 text-zinc-500" />
          </p>
        )}
        {canNativePrompt && (
          <button type="button" onClick={install} data-testid="pwa-install-btn"
            className="mt-2 inline-flex items-center gap-1.5 bg-ag7-purple hover:bg-ag7-purple/85 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-colors">
            <Download className="w-3.5 h-3.5" /> Instalar
          </button>
        )}
      </div>
      <button type="button" onClick={dismiss} aria-label="Cerrar" data-testid="pwa-install-dismiss-btn"
        className="w-7 h-7 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 inline-flex items-center justify-center flex-shrink-0 transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
